import { CoObjectItem } from "../interfaces/CoObject";
import { FullPageModal } from "../layout/FullPageModal";
import { escapeWithBreakLineAndTranslate, formatMessage, getFormattedValue } from "../../utils/helpers/LanguageHelper";
import {
  CollapsableCard,
  CollapsableCardDefaultHeaderComponent,
  CollapsableCardHeaderComponentProps,
} from "../layout/CollapsableCard";
import { TrackedChangeOperationTypePill } from "../generic/TrackedChangeOperationTypePill";
import React, { useEffect, useState } from "react";
import { getTrackedChangesForCoObject } from "../../api/object/CoObjectGeneral";
import { TrackedObjChange } from "../interfaces/ChangesTracker";

const getHeaderComponent = ({
  isExpanded,
  setExpanded,
  changeDate,
  trackedObjChange,
}: CollapsableCardHeaderComponentProps & {
  changeDate: Date;
  trackedObjChange: TrackedObjChange;
}) => (
  <div style={{ display: "flex", width: "100%" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "calc(100% - 2rem)" }}>
      <span style={{ color: "#A3A3A3", fontSize: "12px", fontWeight: "400" }}>
        {changeDate.toLocaleDateString() + " - " + changeDate.toLocaleTimeString()}
      </span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <TrackedChangeOperationTypePill trackedChange={trackedObjChange} />
        <span
          style={{
            color: "#007AC3",
            fontSize: "14px",
            fontWeight: "600",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: "1",
          }}
        >
          {trackedObjChange.username}
        </span>
      </div>
    </div>
    {trackedObjChange.userAction !== "DELETE" && trackedObjChange.differences.length > 0 && (
      <CollapsableCardDefaultHeaderComponent isExpanded={isExpanded} setExpanded={setExpanded} />
    )}
  </div>
);

export const TrackedChangesModal = ({
  trackChangesObjectItem,
  onClose,
}: {
  trackChangesObjectItem: CoObjectItem;
  onClose: () => void;
}) => {
  const [trackedChanges, setTrackedChanges] = useState<TrackedObjChange[]>(undefined);

  function getChanges(logicId: string) {
    getTrackedChangesForCoObject(logicId).then((tracked) => {
      const mappedTrack = tracked.map((el) => {
        const diffs = el.differences.reduce((acc, el) => {
          const currentValue = getFormattedValue(el.currentValue);
          const previousValue = getFormattedValue(el.previousValue);
          if (currentValue?.length || previousValue?.length)
            return [
              ...acc,
              {
                ...el,
                previousValue,
                currentValue,
              },
            ];
          return acc;
        }, []);
        return { ...el, differences: diffs };
      });
      setTrackedChanges(mappedTrack);
    });
  }

  useEffect(() => {
    if (!trackChangesObjectItem) return;

    if (trackChangesObjectItem.id) {
      getChanges(trackChangesObjectItem.id);
    }
  }, [trackChangesObjectItem?.id]);

  return (
    <FullPageModal
      modalProps={{ isOpen: trackChangesObjectItem !== undefined }}
      onClose={onClose}
      textTitle={
        formatMessage("cof.objectlist.changetracker.modal.title") + ` - ${trackChangesObjectItem?.info?.objectName}`
      }
    >
      <div style={{ paddingRight: "10px", paddingLeft: "10px" }}>
        {trackedChanges?.map((c, i) => {
          const changeDate = new Date(c.date);
          return (
            <CollapsableCard
              key={i}
              headerComponent={({ isExpanded, setExpanded }) =>
                getHeaderComponent({ changeDate, trackedObjChange: c, setExpanded, isExpanded })
              }
            >
              {c.differences.map((d) => (
                <>
                  <span style={{ marginTop: "16px", fontSize: "12px", fontWeight: "600" }}>{formatMessage(d.key)}</span>
                  {(d.previousValue as string | null)?.length ? (
                    <div style={{ marginTop: "4px" }}>
                      <span style={{ color: "#007AC3", fontSize: "12px", fontWeight: "600", marginRight: "2px" }}>
                        {formatMessage("cof.objectlist.changetracker.label.previousvalue")}
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: "400" }}>
                        {escapeWithBreakLineAndTranslate(d.previousValue.toString())}
                      </span>
                    </div>
                  ) : null}
                  {(d.currentValue as string | null)?.length ? (
                    <div style={{ marginTop: "4px" }}>
                      <span style={{ color: "#007AC3", fontSize: "12px", fontWeight: "600", marginRight: "2px" }}>
                        {formatMessage("cof.objectlist.changetracker.label.currentvalue")}
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "400",
                        }}
                      >
                        {escapeWithBreakLineAndTranslate(d.currentValue.toString())}
                      </span>
                    </div>
                  ) : null}
                </>
              ))}
            </CollapsableCard>
          );
        })}
      </div>
    </FullPageModal>
  );
};
